/**
 * @file lookup.ts
 * @description Таблица переадресаций магазина и поиск правила для proxy
 * витрины (phase19.16, партия Р3в, v0.19.64; site-builder.md SB131;
 * db-design-r3v §2.1, §5.3, §5.4): типы ответа `GET /public/site-redirects`
 * и его zod-схема (витрина проверяет ответ, непонятный ответ — сбой),
 * встроенные правила платформы `PLATFORM_REDIRECTS` (`/home` → `/`, TD-501),
 * сборка памяти и поиск с переносом строки запроса.
 *
 * Цепочки здесь не разворачиваются: api склеивает их при записи и
 * проходит до 3 шагов при сборке таблицы (§5.3) — витрина делает один шаг.
 */

import { z } from "zod";

import { deepFreeze } from "../site-theme/deep-freeze.js";
import { classifySitePath, isSafeSiteTargetPath, normalizeSiteRedirectPath, SITE_REDIRECT_PATH_MAX } from "./paths.js";

// ── Типы и схема ответа ──────────────────────────────────────────────────

/** Одна действующая переадресация: «откуда» (приведённый путь) → «куда» (свой адрес). */
export interface SiteRedirectItem {
  from: string;
  to: string;
  /**
   * Цель — товар: `/product/<старый>/reviews` уводит на отзывы нового адреса
   * (db-design-r3v §2.1).
   */
  product?: true;
}

/** Таблица магазина: `version` — отпечаток содержимого (он же ETag). */
export interface SiteRedirectTable {
  version: string;
  items: SiteRedirectItem[];
}

/** Предел длины адреса цели (с query), знаков. */
export const SITE_REDIRECT_TARGET_MAX = 2000;

export const siteRedirectItemSchema = z.object({
  from: z
    .string()
    .min(1)
    .max(SITE_REDIRECT_PATH_MAX)
    // В таблицу api кладёт только приведённые адреса; иное — сбой ответа.
    .refine((v) => normalizeSiteRedirectPath(v)?.path === v, "«Откуда» не приведён к виду адреса витрины")
    .refine((v) => classifySitePath(v).kind === "page", "«Откуда» — служебный адрес или раздел"),
  to: z
    .string()
    .min(1)
    .max(SITE_REDIRECT_TARGET_MAX)
    .refine((v) => isSafeSiteTargetPath(v), "«Куда» — только адрес своего сайта"),
  product: z.literal(true).optional(),
});

export const siteRedirectTableSchema = z.object({
  version: z.string().min(1).max(128),
  items: z.array(siteRedirectItemSchema),
});

/** Ответ `GET /api/v1/public/site-redirects` — `{ data: { version, items } }`. */
export const publicSiteRedirectsResponseSchema = z.object({ data: siteRedirectTableSchema });

// Схема и интерфейсы обязаны совпадать: расхождение — ошибка компиляции.
type SchemaItem = z.infer<typeof siteRedirectItemSchema>;
const schemaMatchesItem: (v: SchemaItem) => SiteRedirectItem = (v) => v;
const itemMatchesSchema: (v: SiteRedirectItem) => SchemaItem = (v) => v;
void schemaMatchesItem;
void itemMatchesSchema;

// ── Встроенные правила платформы ─────────────────────────────────────────

/**
 * Правила платформы для каждого магазина без строки в базе. `/home` открывает
 * системную главную вторым адресом — дубль главной для поиска (TD-501,
 * db-design-r3v §5.4, SB131).
 */
export const PLATFORM_REDIRECTS: readonly SiteRedirectItem[] = deepFreeze<SiteRedirectItem[]>([{ from: "/home", to: "/" }]);

// ── Память и поиск ───────────────────────────────────────────────────────

/** Память правил магазина: ключ — приведённый «откуда». */
export type SiteRedirectMap = ReadonlyMap<string, SiteRedirectItem>;

export interface BuildSiteRedirectMapOptions {
  /** Добавить `PLATFORM_REDIRECTS` (по умолчанию да); правило магазина с тем же «откуда» важнее. */
  readonly platform?: boolean;
}

/**
 * Собирает память правил. Строки с неприводимым или служебным «откуда»
 * пропускаются (защита на случай рассинхрона версий api и витрины).
 */
export function buildSiteRedirectMap(
  items: readonly SiteRedirectItem[],
  options: BuildSiteRedirectMapOptions = {},
): SiteRedirectMap {
  const map = new Map<string, SiteRedirectItem>();
  const add = (item: SiteRedirectItem): void => {
    const from = normalizeSiteRedirectPath(item.from);
    if (from === null || classifySitePath(from.path).kind !== "page") return;
    map.set(from.path, item);
  };
  if (options.platform !== false) PLATFORM_REDIRECTS.forEach(add);
  items.forEach(add);
  return map;
}

/** Ключ параметра запроса для сравнения (`utm_source=x` → `utm_source`). */
function queryKey(part: string): string {
  const eq = part.indexOf("=");
  const raw = eq >= 0 ? part.slice(0, eq) : part;
  try {
    return decodeURIComponent(raw.replace(/\+/gu, " "));
  } catch {
    return raw;
  }
}

const queryParts = (query: string): string[] =>
  query
    .replace(/^\?/u, "")
    .split("&")
    .filter((p) => p.length > 0);

/**
 * Объединяет строку запроса посетителя и цели: параметры цели — первыми и
 * важнее (одинаковый ключ берётся у цели), остальные параметры посетителя
 * (`utm_*`) переносятся как были, без перекодирования. Без «?»; пусто — нет.
 */
export function mergeSiteRedirectQuery(requestQuery: string, targetQuery: string): string {
  const target = queryParts(targetQuery);
  const targetKeys = new Set(target.map(queryKey));
  const rest = queryParts(requestQuery).filter((p) => !targetKeys.has(queryKey(p)));
  return [...target, ...rest].join("&");
}

/** Найденное правило и готовый `Location` (относительный адрес своего сайта). */
export interface SiteRedirectHit {
  readonly location: string;
  readonly item: SiteRedirectItem;
}

/**
 * Ищет правило для пути запроса:
 *   - путь приводится так же, как «откуда» при записи (регистр, `%`, `//`, «/»);
 *   - служебные адреса и разделы не переадресуются;
 *   - точное совпадение; иначе `/product/<slug>/reviews` ищет правило
 *     `/product/<slug>` с целью-товаром и уводит на `<цель>/reviews`;
 *   - строка запроса посетителя переносится на цель (`mergeSiteRedirectQuery`);
 *   - цель, ведущая на тот же адрес, — не переадресация (защита от петли);
 *   - цепочки не разворачиваются (таблица сплющена при записи).
 * `query` — строка запроса с «?» или без; если не передана — берётся из `path`.
 */
export function lookupSiteRedirect(map: SiteRedirectMap, path: string, query?: string | null): SiteRedirectHit | null {
  const request = normalizeSiteRedirectPath(path);
  if (request === null) return null;
  const cls = classifySitePath(request.path);
  if (cls.kind !== "page") return null;

  let item = map.get(request.path);
  let suffix = "";
  if (!item && cls.owner.type === "product" && cls.owner.suffix === "reviews") {
    const base = map.get(request.path.slice(0, -"/reviews".length));
    if (base?.product) {
      item = base;
      suffix = "/reviews";
    }
  }
  if (!item || !isSafeSiteTargetPath(item.to)) return null;

  // Цель: путь, своя строка запроса, якорь.
  const hashAt = item.to.indexOf("#");
  const hash = hashAt >= 0 ? item.to.slice(hashAt) : "";
  const noHash = hashAt >= 0 ? item.to.slice(0, hashAt) : item.to;
  const queryAt = noHash.indexOf("?");
  const targetPathRaw = queryAt >= 0 ? noHash.slice(0, queryAt) : noHash;
  const targetQuery = queryAt >= 0 ? noHash.slice(queryAt + 1) : "";
  const targetPath = suffix ? `${targetPathRaw.replace(/\/+$/u, "")}${suffix}` : targetPathRaw;

  if (normalizeSiteRedirectPath(targetPath)?.path === request.path) return null;

  const merged = mergeSiteRedirectQuery(query ?? request.query, targetQuery);
  return { location: `${targetPath}${merged ? `?${merged}` : ""}${hash}`, item };
}
